import { useContext, createContext, useState, useEffect, ReactNode } from 'react';
import { Locale, defaultLocale } from '@/i18n/config';
import { translate } from '@/i18n/translations';

interface TranslationContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export function TranslationProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) {
  // لتجنب اختلاف SSR/CSR نبدأ دائماً باللغة الافتراضية، ثم نحدّث بعد التحميل
  const [locale, setLocaleState] = useState<Locale>(initialLocale || defaultLocale);

  // قراءة اللغة المحفوظة بعد التحميل فقط (لتفادي Hydration mismatch)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const savedLocale = localStorage.getItem('locale') as Locale | null;
    const next = savedLocale && (savedLocale === 'ar' || savedLocale === 'en')
      ? savedLocale
      : (initialLocale || defaultLocale);
    setLocaleState(next);
  }, [initialLocale]);

  // مزامنة السمات وتخزين اللغة
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('locale', locale);
    document.documentElement.setAttribute('lang', locale);
    document.documentElement.setAttribute('dir', locale === 'ar' ? 'rtl' : 'ltr');
    // إضافة class للغة الحالية للمساعدة في CSS
    document.documentElement.classList.remove('lang-ar', 'lang-en');
    document.documentElement.classList.add(`lang-${locale}`);
  }, [locale]);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
  };

  const t = (key: string, params?: Record<string, string | number>) => {
    return translate(key, locale, params);
  };

  return (
    <TranslationContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}

